import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { OrgDataModel } from '../models/orgData.model';
import {
  ReferenceOption,
  SchoolReferenceOption,
} from '../models/reference.option';
import { LangService } from './lang.service';

@Injectable({
  providedIn: 'root',
})
export class ReferenceTableService {
  orgInfo: OrgDataModel;

  constructor(private http: HttpClient, private langService: LangService) {}

  referenceController = '/api/reference';

  noSchoolBoardString = `This student has no School Board associated with their chosen School.
                           They would not have received any School Board consent documents. Please
                           ensure that this student has signed all required documents before enrolling.`;

  // all tables
  referenceTables: any = null;

  // individual tables
  country: ReferenceOption[];
  gender: ReferenceOption[];
  genderQC: ReferenceOption[];
  designation: ReferenceOption[];
  language: ReferenceOption[];
  race: ReferenceOption[];
  relationship: ReferenceOption[];
  province: ReferenceOption[];
  grade: ReferenceOption[];
  school: SchoolReferenceOption[];
  schoolBoard: ReferenceOption[];
  indigenous: ReferenceOption[];
  location: ReferenceOption[] = [];
  stream: ReferenceOption[];
  youthWorkerReason: ReferenceOption[];

  getAllReferenceTables(): Observable<unknown> {
    return this.http
      .get<unknown>(this.referenceController + '/GetAllOptions')
      .pipe(
        map((data) => data),
        shareReplay()
      );
  }

  setAllReferenceTables(): void {
    this.getAllReferenceTables().subscribe((val: any) => {
      this.referenceTables = val;
      this.country = val.country;
      this.gender = val.gender;
      this.genderQC = this.extractQCGender(this.gender);
      this.designation = val.designation;
      this.language = val.language;
      this.race = val.race;
      this.relationship = val.relationship;
      this.province = val.province;
      this.grade = val.grade;
      this.school = val.school;
      this.schoolBoard = val.schoolboard;
      this.indigenous = val.indigenous;
      this.location = val.location;
      this.stream = val.stream;
      this.youthWorkerReason = val.youthWorkerReason;
    });
  }

  async setAllReferenceTablesOnAppLoad() {
    let val: any = await firstValueFrom(this.getAllReferenceTables());
    this.referenceTables = val;
    this.country = val.country;
    this.gender = val.gender;
    this.genderQC = this.extractQCGender(this.gender);
    this.designation = val.designation;
    this.language = val.language;
    this.race = val.race;
    this.relationship = val.relationship;
    this.province = val.province;
    this.grade = val.grade;
    this.school = val.school;
    this.schoolBoard = val.schoolboard;
    this.indigenous = val.indigenous;
    this.location = val.location;
    this.stream = val.stream;
    this.youthWorkerReason = val.youthWorkerReason;
  }

  private extractQCGender(standardGenderObj: any | null): ReferenceOption[] {
    const qcGenderResult = [];
    if (standardGenderObj) {
      let genderM = this.gender.find(
        (gender) => gender.name.toLowerCase() == 'male'
      );
      let genderF = this.gender.find(
        (gender) => gender.name.toLowerCase() == 'female'
      );
      if (genderM) {
        qcGenderResult.push(genderM);
      }
      if (genderF) {
        qcGenderResult.push(genderF);
      }
    }

    return qcGenderResult;
  }

  getValueFromTableById(id: number, table: string): string | null {
    if (!id) {
      return null;
    }

    let found;

    if (!this.referenceTables) {
      return '';
    }

    const foundTable = this.referenceTables[table];

    if (!foundTable) {
      return null;
    }

    found = foundTable.find((data: any) => data.id === id);

    return found.name ?? null;
  }

  getCountry(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.country) {
      this.setAllReferenceTables();
    }
    let found = this.country.find((g) => g.id === id);

    if(found) {
			if(this.langService.currentLang == 'en') {
				return found.name;
			} else {
				return found.nameFr;
			}
		} 

		return null;
  }

  getGender(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.gender) {
      this.setAllReferenceTables();
    }

    let found = this.gender.find((g) => g.id === id);

    return found ? found.name : null;
  }

  getDesignation(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.designation) {
      this.setAllReferenceTables();
    }
    let found = this.designation.find((g) => g.id === id);

    return found ? found.name : null;
  }

  getDesignations(ids: number[]): string {
    if (this.designation && ids && ids.length) {
      const designations = this.designation.filter((i) => ids.includes(i.id));
      return designations.map((i) => i.name).join(', ');
    }

    return '';
  }

  getLanguage(id: number): string | null {
    if (!id) {
      return null;
    }

    if (!this.language) {
      this.setAllReferenceTables();
    }

    let found = this.language.find((g) => g.id === id);

    if(found) {
			if(this.langService.currentLang == 'en') {
				return found.name;
			} else {
				return found.nameFr;
			}
		} 

		return null;
  }

  getLocation(id: number): string | null {
    if (!id) {
      return null;
    }

    if (!this.location) {
      this.setAllReferenceTables();
    }

    let found = this.location.find((g) => g.id == id);

    return found ? found.name : null;
  }

  getLanguages(ids: number[]): string {
    if (this.language && ids && ids.length) {
      const langs = this.language.filter((i) => ids.includes(i.id));
      return langs.map((i) => i.name).join(', ');
    }

    return '';
  }

  getRaces(ids: number[]): string | null {
    if (!ids) {
      return null;
    }
    if (!this.race) {
      this.setAllReferenceTables();
    }
    const foundRaces = [];
    for (let id of ids) {
      let found = this.race.find((r) => r.id === id);

      if (found) {
        foundRaces.push(found.name);
        // if (this.langService.currentLang == 'en') {
        //   foundRaces.push(found.name);
        // } else {
        //   foundRaces.push(found.nameFr);
        // }
      }
    }

    return foundRaces.join(', ');
  }

  getRelationship(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.relationship) {
      this.setAllReferenceTables();
    }

    let found = this.relationship.find((g) => g.id === id);

    return found ? found.name : null;
  }

  private sanitizedProvinceName(name: string): string {
    return name.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }

  getProvince(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.province) {
      this.setAllReferenceTables();
    }

    let found = this.province.find((g) => g.id === id);

    return found ? found.name : null;
  }

  getProvinceId(name: string): number | null {
    return this.province.find(
      (provinceObj) => provinceObj.name == this.sanitizedProvinceName(name)
    ).id;
  }

  getGrade(id: number): string | null {
    if (!id) {
      return null;
    }
    if (!this.grade) {
      this.setAllReferenceTables();
    }
    let found = this.grade.find((g) => g.id === id);

    return found ? found.name : null;
  }

  getSchoolObjByName(name: string) {
    if (!this.school) {
      this.setAllReferenceTables();
    }

    const found = this.school.find((ref) => ref.name === name);

    return found;
  }

  getSchool(id: number): string | null {
    if (!id) {
      return null;
    }

    if (!this.school) {
      this.setAllReferenceTables();
    }

    let found = this.school.find((g) => g.id === id);

    return found ? found.name : null;
  }

  getSchoolsByProvince(id: number): ReferenceOption[] {
    if (!id) {
      return this.school;
    }
    return this.school.filter((s) => s.provinceId === id);
  }

  getSchoolBoardObj(id: number): ReferenceOption | null {
    if (!id) {
      return null;
    }

    if (!this.schoolBoard) {
      this.setAllReferenceTables();
    }

    let found = this.schoolBoard.find((g) => g.id === id);

    return found ? found : null;
  }

  getSchoolBoardName(schoolId: number): string | null {
    if (!this.school) {
      return '';
    }

    if (!schoolId) {
      return null;
    }

    let schoolInfo = this.school.find((s) => s.id === schoolId);
    let schoolBoardInfo = schoolInfo
      ? this.getSchoolBoardObj(schoolInfo.schoolBoardId)
      : null;

    if (schoolBoardInfo) {
      return schoolBoardInfo.name;
    }

    return '';
  }

  getIndigenous(ids: number[]): string | null {
    if (!ids) {
      return null;
    }

    if (!this.indigenous) {
      return '';
    }
    const foundIndigenous = [];
    for (let id of ids) {
      let found = this.indigenous.find((r) => r.id === id);

      if (found) {
        foundIndigenous.push(found.name.replace('Yes,', ''));
      }
    }

    return foundIndigenous.join(', ');
  }

  getOrganization(orgCode: string): string | null {
    if (!orgCode) {
      return null;
    }
    if (!this.location) {
      return '';
    }
    let found = this.location.find((g) => g.code === orgCode);

    return found ? found.name : null;
  }

  getStream(id: number): string | null {
    if (!id) {
      return null;
    }

    if (!this.stream) {
      return '';
    }

    let found = this.stream.find((g) => g.id === id);

    return found ? found.name : null;
  }

  get canadaCountryId() {
    if (!this.country) {
      return null;
    }
    return this.country.find(({ code }) => code === 'CA').id;
  }

  get quebecProvinceId() {
    if (!this.province) {
      return null;
    }
    return this.province.find(({ code }) => code === 'QC').id;
  }
}
